import { Component, type ErrorInfo, type ReactNode } from 'react';
import { errorDetail, flushEvents, track } from '../telemetry.js';
import { reportBrowserError } from '../error-tracking.js';

interface State {
  error?: Error;
}

/**
 * Last-resort screen when the app cannot render, so a rider is never left with a
 * blank page. Reloading never removes the server's trips.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = {};

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Haere could not render.', error, info.componentStack);
    reportBrowserError(error, { kind: 'react', componentStack: info.componentStack });
    const { message, detail } = errorDetail(error);
    track('crash', { message, detail: { ...detail, componentStack: info.componentStack?.slice(0, 4000) } });
    flushEvents();
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="crash-screen" role="alert">
        <div className="crash-card">
          <p className="eyebrow">Haere</p>
          <h1>Something went wrong.</h1>
          <p>The planner hit an error and could not continue. Reloading usually fixes it.</p>
          <p>Your trips are kept on the planner server.</p>
          <div className="crash-actions">
            <button type="button" className="button button-primary" onClick={() => window.location.reload()}>Reload</button>
          </div>
          <details className="crash-details"><summary>Error details</summary><code>{this.state.error.message}</code></details>
        </div>
      </main>
    );
  }
}
